/**
 * Physics-based camera controller for AURA field.
 *
 * Not OrbitControls — too mechanical.
 * This camera has mass, drag, momentum.
 * You don't click-and-move. You push, and it drifts.
 */

import * as THREE from 'three';
import { useAuraStore } from '../state/store';

// Base physics — Sense mode defaults
const BASE_DRAG = 0.92;
const BASE_PAN_SENSITIVITY = 0.003;
const BASE_ZOOM_SENSITIVITY = 0.15;
const MAX_VELOCITY = 50;

// Mode-dependent physics multipliers
// Validate: heavy, resistant — "camera movement feels weighted"
// Create:   fluid, responsive — low friction, easy exploration
const MODE_PHYSICS: Record<string, { drag: number; panMult: number; zoomMult: number }> = {
  sense:    { drag: 0.92,  panMult: 1.0,  zoomMult: 1.0 },
  validate: { drag: 0.78,  panMult: 0.3,  zoomMult: 0.35 },  // locked down — examining a diagram
  create:   { drag: 0.945, panMult: 1.3,  zoomMult: 1.2 },  // lighter drag, more responsive
};
const BOUNDARY_RADIUS = 2000;
const BOUNDARY_SPRING = 0.01;
const DIVE_SPEED = 2.0;
const DIVE_EASING_DURATION = 1500; // ms

interface DiveTarget {
  position: THREE.Vector3;
  startPosition: THREE.Vector3;
  startTarget: THREE.Vector3;
  targetLookAt: THREE.Vector3;
  startTime: number;
}

export class FieldCamera {
  public camera: THREE.PerspectiveCamera;

  private velocity = new THREE.Vector3();
  private targetVelocity = new THREE.Vector3();
  private lookTarget = new THREE.Vector3();
  private isDragging = false;
  private lastMouse = new THREE.Vector2();
  private diveTarget: DiveTarget | null = null;

  // Spherical coordinates for orbit
  private spherical = new THREE.Spherical(800, Math.PI / 2, 0);
  private sphericalVelocity = new THREE.Spherical(0, 0, 0);

  constructor(private container: HTMLElement) {
    this.camera = new THREE.PerspectiveCamera(
      50,
      container.clientWidth / container.clientHeight,
      0.1,
      10000,
    );

    this.camera.position.set(0, 0, 800);
    this.lookTarget.set(0, 0, 0);
    this.camera.lookAt(this.lookTarget);

    this.bindEvents();
  }

  private bindEvents() {
    const el = this.container;

    // Mouse drag for orbit
    el.addEventListener('pointerdown', (e) => {
      if (e.button === 0 || e.button === 1) { // Left or middle
        this.isDragging = true;
        this.lastMouse.set(e.clientX, e.clientY);
        this.diveTarget = null; // Cancel any dive
      }
    });

    el.addEventListener('pointermove', (e) => {
      if (!this.isDragging) return;

      const dx = e.clientX - this.lastMouse.x;
      const dy = e.clientY - this.lastMouse.y;
      this.lastMouse.set(e.clientX, e.clientY);

      // Apply angular velocity (momentum-based, mode-scaled)
      const mode = useAuraStore.getState().mode;
      const panMult = (MODE_PHYSICS[mode] ?? MODE_PHYSICS.sense).panMult;
      this.sphericalVelocity.theta -= dx * BASE_PAN_SENSITIVITY * panMult;
      this.sphericalVelocity.phi -= dy * BASE_PAN_SENSITIVITY * panMult;
    });

    el.addEventListener('pointerup', () => {
      this.isDragging = false;
    });

    el.addEventListener('pointerleave', () => {
      this.isDragging = false;
    });

    // Scroll for zoom
    el.addEventListener('wheel', (e) => {
      e.preventDefault();
      const currentMode = useAuraStore.getState().mode;
      const zoomMult = (MODE_PHYSICS[currentMode] ?? MODE_PHYSICS.sense).zoomMult;
      const delta = e.deltaY * BASE_ZOOM_SENSITIVITY * zoomMult;
      this.sphericalVelocity.radius += delta;
    }, { passive: false });

    // Resize
    const resizeObserver = new ResizeObserver(() => {
      this.camera.aspect = this.container.clientWidth / this.container.clientHeight;
      this.camera.updateProjectionMatrix();
    });
    resizeObserver.observe(this.container);
  }

  /**
   * Smoothly dive toward a point in the field.
   * Used when a user selects a cluster.
   */
  diveTo(worldPosition: THREE.Vector3, offset: number = 50) {
    const store = useAuraStore.getState();
    store.pushCameraState();

    const direction = new THREE.Vector3()
      .subVectors(worldPosition, this.camera.position)
      .normalize();

    const targetPos = worldPosition.clone().sub(direction.multiplyScalar(offset));

    this.diveTarget = {
      position: targetPos,
      startPosition: this.camera.position.clone(),
      startTarget: this.lookTarget.clone(),
      targetLookAt: worldPosition.clone(),
      startTime: performance.now(),
    };
  }

  /**
   * Return to previous camera position (Escape key).
   */
  undive() {
    const prevPos = useAuraStore.getState().popCameraState();
    if (!prevPos) return;

    this.diveTarget = {
      position: new THREE.Vector3(...prevPos),
      startPosition: this.camera.position.clone(),
      startTarget: this.lookTarget.clone(),
      targetLookAt: new THREE.Vector3(0, 0, 0),
      startTime: performance.now(),
    };
  }

  /**
   * Main update loop — called every frame.
   */
  update(_deltaTime: number) {
    // Handle active dive animation
    if (this.diveTarget) {
      const elapsed = performance.now() - this.diveTarget.startTime;
      const t = Math.min(elapsed / DIVE_EASING_DURATION, 1);

      // Cubic bezier easing
      const eased = t < 0.5
        ? 4 * t * t * t
        : 1 - Math.pow(-2 * t + 2, 3) / 2;

      this.camera.position.lerpVectors(
        this.diveTarget.startPosition,
        this.diveTarget.position,
        eased,
      );
      this.lookTarget.lerpVectors(
        this.diveTarget.startTarget,
        this.diveTarget.targetLookAt,
        eased,
      );

      if (t >= 1) {
        this.diveTarget = null;
        // Update spherical from new position
        this.spherical.setFromVector3(
          this.camera.position.clone().sub(this.lookTarget),
        );
      }

      this.camera.lookAt(this.lookTarget);
      return;
    }

    // Mode-dependent camera physics
    const mode = useAuraStore.getState().mode;
    const physics = MODE_PHYSICS[mode] ?? MODE_PHYSICS.sense;

    // Apply velocity with drag
    this.spherical.theta += this.sphericalVelocity.theta;
    this.spherical.phi += this.sphericalVelocity.phi;
    this.spherical.radius += this.sphericalVelocity.radius;

    // Drag — validate is heavier (kills momentum fast), create holds momentum
    this.sphericalVelocity.theta *= physics.drag;
    this.sphericalVelocity.phi *= physics.drag;
    this.sphericalVelocity.radius *= physics.drag;

    // Clamp phi to avoid gimbal lock
    this.spherical.phi = Math.max(0.1, Math.min(Math.PI - 0.1, this.spherical.phi));

    // Clamp radius
    this.spherical.radius = Math.max(50, Math.min(3000, this.spherical.radius));

    // Velocity cap
    const speed = Math.abs(this.sphericalVelocity.theta) + Math.abs(this.sphericalVelocity.phi);
    if (speed > MAX_VELOCITY * BASE_PAN_SENSITIVITY) {
      const scale = (MAX_VELOCITY * BASE_PAN_SENSITIVITY) / speed;
      this.sphericalVelocity.theta *= scale;
      this.sphericalVelocity.phi *= scale;
    }

    // Soft boundary — spring force if too far from origin
    const dist = this.spherical.radius;
    if (dist > BOUNDARY_RADIUS) {
      const overshoot = dist - BOUNDARY_RADIUS;
      this.sphericalVelocity.radius -= overshoot * BOUNDARY_SPRING;
    }

    // Update camera position from spherical
    const offset = new THREE.Vector3().setFromSpherical(this.spherical);
    this.camera.position.copy(this.lookTarget).add(offset);
    this.camera.lookAt(this.lookTarget);

    // Sync to store (throttled — every ~6 frames)
    if (Math.random() < 0.16) {
      const pos = this.camera.position;
      useAuraStore.getState().setCameraPosition([pos.x, pos.y, pos.z]);
    }
  }

  /**
   * Smoothly return to the overview position (0, 0, 800).
   * Used after live data rebuild to reset the viewport.
   */
  resetToOverview() {
    this.diveTarget = {
      position: new THREE.Vector3(0, 0, 800),
      startPosition: this.camera.position.clone(),
      startTarget: this.lookTarget.clone(),
      targetLookAt: new THREE.Vector3(0, 0, 0),
      startTime: performance.now(),
    };
  }

  dispose() {
    // Listeners are on the container, which will be removed
  }
}
